import React, { useId } from 'react';
import { Button, Steps } from 'antd';
import { Check } from 'lucide-react';
import { useBreakpoint } from '@/hooks';
import styles from './GradientPanel.module.scss';

export interface GradientStep {
  key: string;
  label: string;
  state: 'pending' | 'active' | 'done';
}

interface GradientPanelProps {
  title: string;
  steps?: GradientStep[];
  body?: string;
  /** Makes completed steps clickable, jumping back to that step. */
  onStepClick?: (index: number) => void;
}

export const GradientPanel: React.FC<GradientPanelProps> = ({ title, steps, body, onStepClick }) => {
  const { isDesktop } = useBreakpoint();
  const uid = useId().replace(/:/g, '');
  const clipId = `gcl-${uid}`;
  const f0 = `gf0-${uid}`;
  const f1 = `gf1-${uid}`;
  const f2 = `gf2-${uid}`;

  return (
    <div className={styles.grad}>
      <svg
        viewBox="0 0 841 948"
        preserveAspectRatio="xMidYMid slice"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
        className={styles.gradSvg}
      >
        <g clipPath={`url(#${clipId})`}>
          <rect width="841" height="948" fill="#251134" />
          <g filter={`url(#${f0})`}>
            <path
              d="M293.359 177.527C448.814 10.0502 529.049 337.391 584.21 443.967C700.551 535.318 707.906 979.385 697.04 1190H0V672.345C45.9679 563.231 168.995 311.508 293.359 177.527Z"
              fill="#A674E8"
              fillOpacity="0.5"
            />
          </g>
          <g filter={`url(#${f1})`}>
            <path
              d="M636.145 474.189C676.984 548.11 770.729 509.075 812.497 480.317C811.867 539.925 808.661 660.289 800.876 664.888C791.144 670.637 549.662 831.663 525.947 827.288C502.232 822.913 601.761 565.661 493.945 309.979C459.704 228.776 585.096 381.788 636.145 474.189Z"
              fill="#FCD770"
              fillOpacity="0.5"
            />
          </g>
          <g filter={`url(#${f2})`}>
            <path
              d="M524.034 440.793C634.358 810.554 758.152 614.454 817.524 535.851L840.975 858.122L282.627 1086.62C219.56 1114.44 105.385 1155.53 153.224 1097.34C213.022 1024.59 499.335 358.01 524.034 440.793Z"
              fill="#80E59F"
              fillOpacity="0.5"
            />
          </g>
        </g>
        <defs>
          <filter
            id={f0}
            x="-250"
            y="-119.313"
            width="1200.65"
            height="1559.31"
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
            <feGaussianBlur stdDeviation="125" result="effect1_foregroundBlur" />
          </filter>
          <filter
            id={f1}
            x="338.107"
            y="137.002"
            width="624.39"
            height="840.373"
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
            <feGaussianBlur stdDeviation="75" result="effect1_foregroundBlur" />
          </filter>
          <filter
            id={f2}
            x="-108.182"
            y="183.689"
            width="1199.16"
            height="1193.17"
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
            <feGaussianBlur stdDeviation="125" result="effect1_foregroundBlur" />
          </filter>
          <clipPath id={clipId}>
            <rect width="841" height="948" fill="white" />
          </clipPath>
        </defs>
      </svg>
      <div className={styles.gradContent}>
        <p className={styles.gradTitle}>{title}</p>
        {steps && steps.length > 0 && (isDesktop ? (
          <Steps
            className={styles.gradSteps}
            direction="vertical"
            size="small"
            current={steps.findIndex((step) => step.state === 'active')}
            onChange={onStepClick}
            items={steps.map((step) => ({
              key: step.key,
              title: step.label,
              disabled: onStepClick ? step.state !== 'done' : undefined,
              icon: step.state === 'done' ? <Check size={12} strokeWidth={3} /> : undefined,
              styles: {
                icon:
                  step.state === 'active'
                    ? {
                        background: 'var(--color-neutral-1)',
                        borderColor: 'var(--color-neutral-1)',
                        color: 'var(--color-purple-deep)',
                        fontWeight: 600,
                      }
                    : step.state === 'done'
                      ? {
                          background: 'var(--color-green-2)',
                          borderColor: 'transparent',
                          color: 'var(--color-purple-deep)',
                        }
                      : undefined,
                title:
                  step.state === 'active'
                    ? { color: 'var(--color-neutral-1)' }
                    : step.state === 'done'
                      ? { color: 'var(--color-green-2)' }
                      : undefined,
                rail: step.state === 'done' ? { borderColor: 'var(--color-green-2)' } : undefined,
              },
            }))}
          />
        ) : (
          <div className={styles.gradStepsMobile}>
            <div
              className={styles.gradStepsMobileRow}
              role="progressbar"
              aria-valuenow={steps.findIndex((step) => step.state === 'active') + 1}
              aria-valuemin={1}
              aria-valuemax={steps.length}
              aria-valuetext={steps.find((step) => step.state === 'active')?.label}
            >
              {steps.map((step, i) => (
                <React.Fragment key={step.key}>
                  {step.state === 'done' && onStepClick ? (
                    <Button
                      shape="circle"
                      className={`${styles.gradDot} ${styles.gradDotDone} ${styles.gradDotButton}`}
                      onClick={() => onStepClick(i)}
                      aria-label={`Back to ${step.label}`}
                      icon={<Check size={12} strokeWidth={3} aria-hidden="true" />}
                    />
                  ) : (
                    <span
                      className={
                        step.state === 'active'
                          ? `${styles.gradDot} ${styles.gradDotActive}`
                          : step.state === 'done'
                            ? `${styles.gradDot} ${styles.gradDotDone}`
                            : styles.gradDot
                      }
                      aria-hidden="true"
                    >
                      {step.state === 'done' ? <Check size={12} strokeWidth={3} /> : i + 1}
                    </span>
                  )}
                  {i < steps.length - 1 && (
                    <span
                      className={step.state === 'done' ? `${styles.gradRail} ${styles.gradRailDone}` : styles.gradRail}
                      aria-hidden="true"
                    />
                  )}
                </React.Fragment>
              ))}
            </div>
            <p className={styles.gradStepsMobileLabel}>
              {steps.find((step) => step.state === 'active')?.label}
            </p>
          </div>
        ))}
        {body && <p className={styles.gradBody}>{body}</p>}
      </div>
    </div>
  );
};

export default GradientPanel;
